import { CurrencyPipe, DatePipe, I18nPluralPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core'
import { BookingProductRefundPolicy, BookingProductRefundPolicyComponent } from './booking-product-refund-policy.component'
import { CurrencyUnitPipe } from '../../../pipes/currency-unit.pipe'

export interface VenueStorefrontBookingSummaryChoice {
    displayName: string
}

export interface VenueStorefrontBookingSummaryExtra extends BookingProductRefundPolicy {
    name: string
    quantity: number
    unitAmount?: number
    modifierNames?: string[]
}

export interface VenueStorefrontBookingSummaryPayment {
    kind: 'card' | 'deposit'
    amount: number
    isPerCover?: boolean
    label: string
}

export interface VenueStorefrontBookingSummary {
    venueName: string
    venueImageUrl: string | null
    partySize: number
    dateTime: Date
    endDateTime: Date
    areaName: string | null
    previous?: Pick<VenueStorefrontBookingSummary, 'partySize' | 'dateTime' | 'endDateTime' | 'areaName'>
    currencyCode: string | null
    items?: {
        id: string
        reasonName: string
        imageUrl?: string | null
        areaName?: string | null
        start: Date
        end: Date
        depositAmount: number
        orderAmount?: number
        paymentLabel?: string
        extras: VenueStorefrontBookingSummaryExtra[]
    }[]
    choice?: VenueStorefrontBookingSummaryChoice
    extras: VenueStorefrontBookingSummaryExtra[]
    payment?: VenueStorefrontBookingSummaryPayment
}

@Component({
    selector: 'app-venue-storefront-booking-summary-card',
    standalone: true,
    imports: [CurrencyUnitPipe, DatePipe, I18nPluralPipe, BookingProductRefundPolicyComponent],
    providers: [CurrencyPipe],
    templateUrl: './venue-storefront-booking-summary-card.component.html',
    styleUrls: ['./venue-storefront-booking-summary-card.component.sass'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VenueStorefrontBookingSummaryCardComponent {
    summary = input.required<VenueStorefrontBookingSummary>()

    protected readonly partySizePluralMapping = {
        '=1': '1 person',
        other: '# people',
    }

    protected readonly paymentTotal = computed(() => {
        const summary = this.summary()
        const payment = summary.payment
        if (!payment) {
            return 0
        }
        return payment.amount * (payment.isPerCover ? summary.partySize : 1)
    })

    protected readonly endsOnDifferentDay = computed(() => {
        const { dateTime, endDateTime } = this.summary()
        return dateTime.getFullYear() !== endDateTime.getFullYear()
            || dateTime.getMonth() !== endDateTime.getMonth()
            || dateTime.getDate() !== endDateTime.getDate()
    })

    protected readonly changes = computed(() => {
        const current = this.summary()
        const previous = current.previous
        const timeOfDay = (date: Date) => date.getHours() * 60 + date.getMinutes()
        return {
            date: !!previous && previous.dateTime.toDateString() !== current.dateTime.toDateString(),
            time: !!previous && (
                timeOfDay(previous.dateTime) !== timeOfDay(current.dateTime)
                || timeOfDay(previous.endDateTime) !== timeOfDay(current.endDateTime)
                || ((previous.dateTime.toDateString() !== previous.endDateTime.toDateString() || this.endsOnDifferentDay())
                    && previous.endDateTime.toDateString() !== current.endDateTime.toDateString())
            ),
            partySize: !!previous && previous.partySize !== current.partySize,
            area: !!previous && previous.areaName !== current.areaName,
        }
    })
}
